import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from 'recharts'
import { useMemo, useState } from 'react'
import { TrendingUp, TrendingDown, Eye, BookOpen, Briefcase, FileText } from 'lucide-react'
import { supabase } from '../supabaseClient'
import { getPeriodDates, formatDate } from '../lib/helpers'

function StatCard({ label, value, delta, icon, color }) {
  const up = delta >= 0
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: color + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
          }}
        >
          {icon}
        </div>
        {typeof delta === 'number' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '13px',
              fontWeight: 600,
              color: up ? '#10b981' : '#ef4444',
              backgroundColor: up ? '#ecfdf5' : '#fef2f2',
              padding: '4px 10px',
              borderRadius: '20px',
            }}
          >
            {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {up ? '+' : ''}{delta}%
          </div>
        )}
      </div>
      <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#0d1f12' }}>{value}</div>
    </div>
  )
}

export default function DashboardPage() {
  const [period, setPeriod] = useState('30d')
  const [data, setData] = useState([])
  const [topPages, setTopPages] = useState([])
  const [stats, setStats] = useState({ total: 0, prevTotal: 0, blogViews: 0, totalPosts: 0, totalCases: 0 })

  useMemo(() => {
    const fetch = async () => {
      const { start, end, prevStart, prevEnd } = getPeriodDates(period)

      const { data: current } = await supabase
        .from('page_views')
        .select('path, logged_at')
        .gte('logged_at', start.toISOString())
        .lte('logged_at', end.toISOString())

      const { data: previous } = await supabase
        .from('page_views')
        .select('id')
        .gte('logged_at', prevStart.toISOString())
        .lte('logged_at', prevEnd.toISOString())

      const { count: totalPosts } = await supabase.from('posts').select('*', { count: 'exact', head: true }).eq('status', 'published')
      const { count: totalCases } = await supabase.from('cases').select('*', { count: 'exact', head: true }).eq('status', 'published')
      const { data: blogViews } = await supabase.from('page_views').select('id').ilike('path', '/blog/%').gte('logged_at', start.toISOString()).lte('logged_at', end.toISOString())

      const currentViews = current || []
      const prevTotal = previous?.length || 0

      const grouped = {}
      currentViews.forEach((row) => {
        const d = row.logged_at.slice(0, 10)
        grouped[d] = (grouped[d] || 0) + 1
      })
      const chartData = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([date, views]) => ({ date, views }))

      const pageCounts = {}
      currentViews.forEach((row) => { pageCounts[row.path] = (pageCounts[row.path] || 0) + 1 })
      const top = Object.entries(pageCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([path, views]) => ({ path, views }))

      const total = currentViews.length
      const delta = prevTotal > 0 ? Math.round(((total - prevTotal) / prevTotal) * 100) : 0

      setData(chartData)
      setTopPages(top)
      setStats({ total, prevTotal, delta, blogViews: blogViews?.length || 0, totalPosts: totalPosts || 0, totalCases: totalCases || 0 })
    }
    fetch()
  }, [period])

  const periods = [
    { key: 'today', label: 'Hoje' },
    { key: 'yesterday', label: 'Ontem' },
    { key: '7d', label: '7 dias' },
    { key: '15d', label: '15 dias' },
    { key: '30d', label: '30 dias' },
    { key: '60d', label: '60 dias' },
    { key: '90d', label: '90 dias' },
    { key: 'year', label: 'Anual' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Period Selector */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {periods.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            style={{
              padding: '8px 16px',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: period === p.key ? '#0d1f12' : '#ffffff',
              color: period === p.key ? '#bef102' : '#9ca3af',
              boxShadow: period === p.key ? '0 2px 8px rgba(13,31,18,0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
        <StatCard label="Total de acessos" value={stats.total.toLocaleString('pt-BR')} delta={stats.delta} icon={<Eye size={22} />} color="#0d1f12" />
        <StatCard label="Visualizações Blog" value={stats.blogViews.toLocaleString('pt-BR')} icon={<BookOpen size={22} />} color="#3b82f6" />
        <StatCard label="Posts publicados" value={stats.totalPosts} icon={<FileText size={22} />} color="#8b5cf6" />
        <StatCard label="Cases publicados" value={stats.totalCases} icon={<Briefcase size={22} />} color="#10b981" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0d1f12', marginBottom: '20px' }}>Evolução diária de acessos</h3>
          <div style={{ height: '280px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#bef102" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#bef102" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: '#fff', borderColor: '#f3f4f6', color: '#0d1f12', fontSize: 13, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(v) => [`${v} acessos`, 'Acessos']}
                  labelFormatter={(l) => formatDate(l)}
                />
                <Area type="monotone" dataKey="views" stroke="#bef102" fillOpacity={1} fill="url(#colorViews)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0d1f12', marginBottom: '20px' }}>Páginas mais visitadas</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {topPages.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: '#f0fdf4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#0d1f12',
                    }}
                  >
                    {i + 1}
                  </div>
                  <span style={{ fontSize: '14px', color: '#374151', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.path}>
                    {p.path}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#0d1f12',
                    backgroundColor: '#bef102',
                    padding: '4px 12px',
                    borderRadius: '20px',
                  }}
                >
                  {p.views}
                </span>
              </div>
            ))}
            {topPages.length === 0 && <p style={{ fontSize: '14px', color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Sem dados para o período.</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
